<template>
  <v-dialog v-model="dialog" max-width="860px" scrollable @click:outside="$emit('close')">
    <v-card v-if="tournament">
      <v-img
        v-if="tournament.image"
        :src="tournament.image"
        max-height="220"
        class="white--text align-end"
        gradient="to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,.6)"
      >
        <v-card-title class="text-h5">{{ tournament.name }}</v-card-title>
      </v-img>
      <v-card-title v-else class="primary--text">
        {{ tournament.name }}
      </v-card-title>

      <div class="px-6 pt-3">
        <v-chip :color="status.color" small text-color="white" class="mr-2">
          {{ status.text }}
        </v-chip>
        <v-chip small outlined class="mr-2">
          <v-icon left x-small>{{ typeIcon }}</v-icon>
          {{ typeLabel }}
        </v-chip>
        <v-chip v-if="categoryLabel" small outlined class="mr-2">{{ categoryLabel }}</v-chip>
        <v-chip v-if="gameSystemLabel" small outlined>{{ gameSystemLabel }}</v-chip>
      </div>

      <v-card-text class="pa-6">
        <v-row>
          <!-- Fechas y lugar -->
          <v-col cols="12" sm="6">
            <div class="label">Fecha</div>
            <div class="value">
              <v-icon small>mdi-calendar</v-icon>
              {{ dateRange }}
            </div>

            <div v-if="timeRange" class="label mt-4">Horario</div>
            <div v-if="timeRange" class="value">
              <v-icon small>mdi-clock-outline</v-icon>
              {{ timeRange }}
              <v-chip v-if="tournament.starting_shotgun" x-small color="info" text-color="white" class="ml-1">
                Escopetazo{{ tournament.shotgun_time ? ' ' + shortTime(tournament.shotgun_time) : '' }}
              </v-chip>
            </div>

            <div class="label mt-4">Cancha</div>
            <div class="value">
              <v-icon small>mdi-golf</v-icon>
              {{ courses }}
            </div>

            <div class="label mt-4">Handicap</div>
            <div class="value">
              <v-icon small>mdi-chart-line</v-icon>
              {{ handicapText }}
            </div>
          </v-col>

          <!-- Cupo y registro -->
          <v-col cols="12" sm="6">
            <div class="label">Inscritos</div>
            <div class="value">
              {{ tournament.current_players || 0 }} / {{ tournament.max_players }}
              <span v-if="!tournament.is_full" class="success--text text-body-2 ml-2">
                {{ tournament.available_slots }} disponibles
              </span>
            </div>
            <v-progress-linear
              :value="participation"
              height="8"
              rounded
              class="mt-2"
              :color="tournament.is_full ? 'error' : 'primary'"
            />

            <div class="label mt-4">Registro</div>
            <div class="value">
              <v-icon small :color="tournament.is_registration_open ? 'success' : 'grey'">
                {{ tournament.is_registration_open ? 'mdi-lock-open-variant' : 'mdi-lock' }}
              </v-icon>
              {{ tournament.is_registration_open ? 'Abierto' : 'Cerrado' }}
            </div>
            <div v-if="tournament.registration_open_at" class="text-caption grey--text">
              Abre: {{ formatDateTime(tournament.registration_open_at) }}
            </div>
            <div v-if="tournament.registration_close_at" class="text-caption grey--text">
              Cierra: {{ formatDateTime(tournament.registration_close_at) }}
            </div>

            <div class="label mt-4">Reglas</div>
            <div class="value">
              <v-icon small>mdi-account-multiple-plus</v-icon>
              Hasta {{ tournament.max_guests_per_member }} invitado(s) por socio
            </div>
            <div class="text-caption grey--text">
              {{ tournament.allow_children ? 'Permite registrar niños' : 'No permite niños' }}
            </div>
          </v-col>
        </v-row>

        <!-- Descripción -->
        <template v-if="tournament.description">
          <v-divider class="my-4" />
          <div class="label">Descripción</div>
          <p class="mt-2 mb-0" style="white-space: pre-line">{{ tournament.description }}</p>
        </template>

        <!-- Salidas -->
        <template v-if="teeTimes.length">
          <v-divider class="my-4" />
          <div class="label">Salidas</div>
          <div class="mt-2">
            <v-chip
              v-for="(time, index) in teeTimes"
              :key="index"
              outlined
              small
              class="mr-2 mb-2"
            >
              {{ time.hour }}<span v-if="time.slots"> · {{ time.slots }} salidas</span>
            </v-chip>
          </div>
        </template>

        <!-- Enlace de registro -->
        <template v-if="tournament.public_registration_url">
          <v-divider class="my-4" />
          <div class="label">Enlace de registro</div>
          <v-text-field
            :value="tournament.public_registration_url"
            readonly
            dense
            outlined
            hide-details
            class="mt-2"
          >
            <template #append>
              <v-btn icon small title="Copiar enlace" @click="copyLink">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="flex-wrap">
        <template v-if="isRp">
          <v-btn text color="primary" @click="$emit('participants', tournament)">
            <v-icon left small>mdi-account-group</v-icon>
            Participantes
          </v-btn>
          <v-btn text @click="$emit('edit', tournament)">
            <v-icon left small>mdi-pencil</v-icon>
            Editar
          </v-btn>
        </template>
        <v-spacer />
        <v-btn text @click="$emit('close')">Cerrar</v-btn>
        <v-btn
          v-if="canRegister"
          color="primary"
          @click="$emit('register', tournament)"
        >
          Inscribirse
        </v-btn>
        <v-chip v-else-if="isRegistered" color="success" small text-color="white">
          Ya estás inscrito
        </v-chip>
        <v-chip v-else-if="tournament.is_full" color="error" small text-color="white">
          Torneo lleno
        </v-chip>
      </v-card-actions>

      <v-snackbar v-model="copied" :timeout="2000" color="success">
        Enlace copiado
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  getTournamentStatus,
  getCategoryById,
  getGameSystemById,
  TOURNAMENT_TYPES,
  REGISTRATION_OPEN_STATUS
} from '../constants/tournamentConfig'

export default {
  name: 'TournamentDetailDialog',
  props: {
    tournament: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    isRegistered: {
      type: Boolean,
      default: false
    },
    isRp: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: true,
      copied: false
    }
  },
  computed: {
    status () {
      return getTournamentStatus(this.tournament.status)
    },
    typeLabel () {
      const type = TOURNAMENT_TYPES.find(t => t.value === this.tournament.tournament_type)
      return type ? type.text : 'Adultos'
    },
    typeIcon () {
      return this.tournament.tournament_type === 'kids' ? 'mdi-human-child' : 'mdi-account'
    },
    categoryLabel () {
      const t = this.tournament
      const cat = getCategoryById(t.category)
      return t.category_display || (cat && cat.name) || ''
    },
    gameSystemLabel () {
      const t = this.tournament
      const sys = getGameSystemById(t.game_system)
      return t.game_system_display || (sys && sys.name) || ''
    },
    courses () {
      const detail = this.tournament.courses_detail
      if (detail && detail.length) return detail.map(c => c.name).join(', ')
      return this.tournament.course || 'Por definir'
    },
    dateRange () {
      const start = this.tournament.start_date
      const end = this.tournament.end_date
      if (!start) return 'Por definir'
      if (!end || end === start) return this.formatDate(start)
      return `${this.formatDate(start)} al ${this.formatDate(end)}`
    },
    timeRange () {
      const { start_time: s, end_time: e } = this.tournament
      if (!s) return ''
      return e ? `${this.shortTime(s)} – ${this.shortTime(e)}` : this.shortTime(s)
    },
    handicapText () {
      const t = this.tournament
      if (t.handicap_free) return 'Libre'
      if (t.handicap_min == null && t.handicap_max == null) return 'Sin restricción'
      return `${t.handicap_min} a ${t.handicap_max}`
    },
    teeTimes () {
      return Array.isArray(this.tournament.tee_times) ? this.tournament.tee_times : []
    },
    participation () {
      const max = this.tournament.max_players
      if (!max) return 0
      return Math.min(100, ((this.tournament.current_players || 0) / max) * 100)
    },
    canRegister () {
      if (this.isRp) return true
      return !this.isRegistered && !this.tournament.is_full &&
        this.tournament.status === REGISTRATION_OPEN_STATUS
    }
  },
  methods: {
    // Las fechas llegan como YYYY-MM-DD: se parsean como locales para evitar el desfase de zona horaria.
    parseDate (value) {
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number)
        return new Date(y, m - 1, d)
      }
      return new Date(value)
    },
    formatDate (value) {
      return this.parseDate(value).toLocaleDateString('es-MX', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    },
    formatDateTime (value) {
      return new Date(value).toLocaleString('es-MX', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    shortTime (value) {
      return value ? String(value).slice(0, 5) : ''
    },
    async copyLink () {
      try {
        await navigator.clipboard.writeText(this.tournament.public_registration_url)
        this.copied = true
      } catch (e) {
        // Sin permiso de portapapeles: el campo es de solo lectura y se puede copiar a mano.
      }
    }
  }
}
</script>

<style scoped>
.label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
}
.value {
  font-size: 1rem;
}
::v-deep .v-dialog__container {
  align-items: flex-start;
  padding-top: 20px;
}
</style>
